import React, { useState } from 'react';
import { PageSection, ContentWrapper, ContentWide } from 'volto-essvoltocomponents/components/Layout';

// Molecules (Custom Components)
import TeaserProfile from '../../Molecules/TeaserProfile';
import Accordion from '../../Molecules/Accordion/Accordion';
import { CTABanner } from '../../Molecules/CTABanner';
// ESS Design System Components (@plone/components based)
import { ESSProgressBar } from '../../Atoms/ProgressBar';
import { ESSTagGroup, Tag } from '../../Atoms/TagGroup';
import { ESSButton } from '../../Atoms/ESSButton';
// Form Components (TypeScript)
import { ESSTextField } from '../../Atoms/ESSTextField';
import { ESSComboBox } from '../../Atoms/ESSComboBox';
import { ESSSwitch } from '../../Atoms/ESSSwitch';
import { ESSCheckbox } from '../../Atoms/ESSCheckbox';
import { ESSRadioGroup } from '../../Atoms/ESSRadioGroup';
import { ESSDatePicker } from '../../Atoms/ESSDatePicker';
import Dropdown from '../../Molecules/DropDown/Dropdown';
// Interactive Examples
import ToastExample from '../../Examples/ToastExample';
import TooltipExample from '../../Examples/TooltipExample';
import MenuExample from '../../Examples/MenuExample';
import DialogExample from '../../Examples/DialogExample';
import TabsExample from '../../Examples/TabsExample';
import CookieConsentExample from '../../Organisms/CookieConsent/CookieConsentExample';
import './ComponentsShowcase.scss';
import Logo from '../../../customizations/components/theme/Logo/Logo';
import BscLogoJpg from '../../../images/bsc-logo.jpg';
import BscLogoSvg from '../../../images/BSC-blue.svg';

/**
 * ComponentsShowcase - ESS Design System Components
 * Simplified showcase using @plone/components as foundation
 */
const ComponentsShowcase = () => {
  const [inputValue, setInputValue] = useState('');
  const [checkboxValue, setCheckboxValue] = useState(false);
  const [selectValue, setSelectValue] = useState('');

  const exMenu = [
    { id: '1', label: 'First action', onSelect: () => {} },
    { id: '2', label: 'Second action', onSelect: () => {} },
    { id: '3', label: 'Third action', onSelect: () => {} },
    { id: '4', label: 'Fourth action', onSelect: () => {} },
    { id: '5', label: 'Fifth action', onSelect: () => {} },
    { id: '6', label: 'Sixth action', onSelect: () => {} },
    { id: '7', label: 'Seventh action', onSelect: () => {} },
    { id: '8', label: 'Eight action', onSelect: () => {} },
    { id: '9', label: 'Nine action', onSelect: () => {} },
    { id: '10', label: 'Lorem Ipsum is simply dummy text', onSelect: () => {} },
  ];

  return (
    <div className="components-showcase">
      <PageSection>
        <ContentWrapper>
          <ContentWide>
            <div className="components-showcase__header">
              <h1 className="components-showcase__title">
                ESS Design System Components
              </h1>
              <p className="components-showcase__description">
                Component showcase for the ESS Design System based on
                @plone/components
              </p>
            </div>

            {/* ========================================
                ESS DESIGN SYSTEM COMPONENTS
                Based on @plone/components
            ======================================== */}

            {/* Tabs Component */}
            <section className="components-showcase__section">
              <h2 className="components-showcase__section-title">
                Tabs - Tabbed Interfaces
              </h2>
              <p className="components-showcase__description">
                Click on tabs to switch between different content panels. Shows 3
                variants: default, pills, and underline.
              </p>
              <div className="components-showcase__full-width">
                <TabsExample />
              </div>
            </section>

            {/* Buttons Component */}
            <section className="components-showcase__section">
              <h2 className="components-showcase__section-title">
                Buttons - Interactive Actions
              </h2>
              <p className="components-showcase__description">
                Modern button component with 3 variants (positive, negative,
                accent), customizable colors, border radius, and icon support
              </p>

              <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Variants</h3>
              <div className="components-showcase__grid">
                <div className="components-showcase__item">
                  <h4>Positive</h4>
                  <ESSButton variant="positive" text="Confirm" />
                </div>
                <div className="components-showcase__item">
                  <h4>Negative</h4>
                  <ESSButton variant="negative" text="Delete" />
                </div>
                <div className="components-showcase__item">
                  <h4>Accent</h4>
                  <ESSButton variant="accent" text="Learn More" />
                </div>
              </div>

              <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>
                With Icons
              </h3>
              <div className="components-showcase__grid">
                <div className="components-showcase__item">
                  <h4>Icon + Text</h4>
                  <ESSButton
                    variant="positive"
                    text="Save"
                    icon={<span>💾</span>}
                  />
                </div>
                <div className="components-showcase__item">
                  <h4>Icon Only</h4>
                  <ESSButton variant="positive" icon={<span>✓</span>} />
                </div>
                <div className="components-showcase__item">
                  <h4>Icon + Text (Negative)</h4>
                  <ESSButton
                    variant="negative"
                    text="Remove"
                    icon={<span>🗑️</span>}
                  />
                </div>
              </div>

              <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>
                Border Radius
              </h3>
              <div className="components-showcase__grid">
                <div className="components-showcase__item">
                  <h4>No Radius (0)</h4>
                  <ESSButton
                    variant="positive"
                    text="Square Button"
                    borderRadius={0}
                  />
                </div>
                <div className="components-showcase__item">
                  <h4>Small Radius (4px)</h4>
                  <ESSButton
                    variant="positive"
                    text="Slightly Rounded"
                    borderRadius={4}
                  />
                </div>
                <div className="components-showcase__item">
                  <h4>Large Radius (20px)</h4>
                  <ESSButton
                    variant="positive"
                    text="Very Rounded"
                    borderRadius={20}
                  />
                </div>
              </div>

              <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>
                Custom Colors
              </h3>
              <div className="components-showcase__grid">
                <div className="components-showcase__item">
                  <h4>Custom Background</h4>
                  <ESSButton
                    variant="positive"
                    text="Custom Blue"
                    backgroundColor="#1e40af"
                    textColor="#ffffff"
                  />
                </div>
                <div className="components-showcase__item">
                  <h4>Custom Accent</h4>
                  <ESSButton
                    variant="accent"
                    text="Custom Purple"
                    backgroundColor="#7c3aed"
                    textColor="#ffffff"
                  />
                </div>
                <div className="components-showcase__item">
                  <h4>Custom Negative</h4>
                  <ESSButton
                    variant="negative"
                    text="Custom Orange"
                    backgroundColor="#ea580c"
                    textColor="#ffffff"
                  />
                </div>
              </div>

              <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Sizes</h3>
              <div className="components-showcase__grid">
                <div className="components-showcase__item">
                  <h4>Small</h4>
                  <ESSButton variant="positive" text="Small Button" size="sm" />
                </div>
                <div className="components-showcase__item">
                  <h4>Medium (Default)</h4>
                  <ESSButton variant="positive" text="Medium Button" size="md" />
                </div>
              </div>

              <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>States</h3>
              <div className="components-showcase__grid">
                <div className="components-showcase__item">
                  <h4>Loading</h4>
                  <ESSButton variant="positive" text="Loading..." loading />
                </div>
                <div className="components-showcase__item">
                  <h4>Disabled</h4>
                  <ESSButton variant="positive" text="Disabled" disabled />
                </div>
                <div className="components-showcase__item">
                  <h4>Full Width</h4>
                  <ESSButton
                    variant="positive"
                    text="Full Width Button"
                    fullWidth
                  />
                </div>
              </div>
              <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Layout</h3>
                <div className="components-showcase__grid">
                  <div className="components-showcase__item">
                    <h4>Long text</h4>
                    <ESSButton
                      variant="positive"
                      text="Lorem Ipsum is simply dummy text"
                      fullWidth
                    />
                  </div>
                  <div className="components-showcase__item"></div>
                  <div className="components-showcase__item"></div>
              </div>
            </section>

            {/* Progress Component */}
            <section className="components-showcase__section">
              <h2 className="components-showcase__section-title">
                Progress - Visual Indicators
              </h2>
              <p className="components-showcase__description">
                Visual progress indicators with different variants and animations
              </p>
              <div className="components-showcase__grid">
                <div className="components-showcase__item">
                  <h3>Default Progress</h3>
                  <ESSProgressBar value={60} />
                </div>
                <div className="components-showcase__item">
                  <h3>Success</h3>
                  <ESSProgressBar value={100} variant="success" />
                </div>
                <div className="components-showcase__item">
                  <h3>Warning</h3>
                  <ESSProgressBar value={45} variant="warning" />
                </div>
                <div className="components-showcase__item">
                  <h3>Danger</h3>
                  <ESSProgressBar value={25} variant="danger" />
                </div>
              </div>
            </section>

            {/* Tags Component */}
            <section className="components-showcase__section">
              <h2 className="components-showcase__section-title">
                Tags - Grouped Labels
              </h2>
              <p className="components-showcase__description">
                Grouped tags with different variants and styles
              </p>
              <div className="components-showcase__grid">
                <div className="components-showcase__item">
                  <h3>Default Tags</h3>
                  <ESSTagGroup variant="default">
                    <Tag>React</Tag>
                    <Tag>TypeScript</Tag>
                    <Tag>Plone</Tag>
                  </ESSTagGroup>
                </div>
                <div className="components-showcase__item">
                  <h3>Outlined Tags</h3>
                  <ESSTagGroup variant="outlined">
                    <Tag>Design</Tag>
                    <Tag>System</Tag>
                    <Tag>ESS</Tag>
                  </ESSTagGroup>
                </div>
              </div>
            </section>

            {/* ========================================
                FORM COMPONENTS
                TypeScript-based form controls
            ======================================== */}

            {/* Form Controls Section */}
            <section className="components-showcase__section">
              <h2 className="components-showcase__section-title">
                Form Controls - TypeScript Components
              </h2>
              <p className="components-showcase__description">
                Production-ready form components with native HTML elements and ESS
                styling
              </p>

              {/* TextField */}
              <div className="components-showcase__subsection">
                <h3 className="components-showcase__subsection-title">TextField</h3>
                <div className="components-showcase__grid components-showcase__grid--form">
                  <div className="components-showcase__item">
                    <h4>Default</h4>
                    <ESSTextField
                      label="Email Address"
                      placeholder="Enter your email"
                      type="email"
                    />
                  </div>
                  <div className="components-showcase__item">
                    <h4>With Helper Text</h4>
                    <ESSTextField
                      label="Username"
                      placeholder="Choose a username"
                      helperText="Must be unique and at least 3 characters"
                    />
                  </div>
                  <div className="components-showcase__item">
                    <h4>Required</h4>
                    <ESSTextField
                      label="Full Name"
                      placeholder="Enter your name"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* #287: TextField sm vs Dropdown sm height */}
              <div className="components-showcase__subsection">
                <h3 className="components-showcase__subsection-title">
                  sm height comparison
                </h3>
                <p className="components-showcase__description">
                  TextField sm vs Dropdown sm. The bordered controls should be the same height.
                </p>
                <div className="components-showcase__grid components-showcase__grid--form">
                  <div className="components-showcase__item">
                    <h4>TextField sm (like /search)</h4>
                    <ESSTextField placeholder="Search" size="sm" />
                  </div>
                  <div className="components-showcase__item">
                    <h4>Dropdown sm</h4>
                    <Dropdown
                      label="Small dropdown"
                      triggerSize="sm"
                      items={exMenu}
                    />
                  </div>
                  <div className="components-showcase__item">
                    <h4>TextField sm with label</h4>
                    <ESSTextField
                      label="Filter"
                      placeholder="Filter by text"
                      size="sm"
                    />
                  </div>
                </div>
              </div>

              {/* Dropdown */}
              <div className="components-showcase__subsection">
                <h3 className="components-showcase__subsection-title">Dropdown</h3>
                <p className="components-showcase__description">
                  Menu button with button size variants (and default option list size, the same than the button size).
                </p>
                <div className="components-showcase__grid components-showcase__grid--form">
                  <div className="components-showcase__item">
                    <h4>Button size Small</h4>
                    <Dropdown
                      label="Small dropdown"
                      triggerSize="sm"
                      items={exMenu}
                    />
                  </div>
                  <div className="components-showcase__item">
                    <h4>Button size Medium</h4>
                    <Dropdown
                      label="Medium dropdown"
                      triggerSize="md"
                      items={exMenu}
                    />
                  </div>
                  <div className="components-showcase__item">
                    <h4>Button size Large</h4>
                    <Dropdown
                      label="Large dropdown"
                      triggerSize="lg"
                      items={exMenu}
                    />
                  </div>
                </div>

                <p className="components-showcase__description">
                  Menu button with button size variants and parametrized option list size that in these examples is smaller than the button size.
                </p>
                <div className="components-showcase__grid components-showcase__grid--form">
                  <div className="components-showcase__item">
                    <h4>Button size Medium and option list size Small</h4>
                    <Dropdown
                      label="Medium dropdown"
                      triggerSize="md"
                      menuItemSize="sm"
                      items={exMenu}
                    />
                  </div>
                  <div className="components-showcase__item">
                    <h4>Button size Large and option list size Medium</h4>
                    <Dropdown
                      label="Large dropdown"
                      size="lg"
                      triggerSize="lg"
                      menuItemSize="md"
                      items={exMenu}
                    />
                  </div>
                  <div className="components-showcase__item">
                    <h4>With short option list (vertical scroll bar does not appear)</h4>
                    <Dropdown
                      label="Short dropdown list"
                      items={[
                        { id: '1', label: 'First action', onSelect: () => {} },
                        { id: '2', label: 'Second action', onSelect: () => {} },
                      ]}                    
                    />
                  </div>
                </div>
              </div>

              {/* Checkbox */}
              <div className="components-showcase__subsection">
                <h3 className="components-showcase__subsection-title">Checkbox</h3>
                <div className="components-showcase__grid components-showcase__grid--form">
                  <div className="components-showcase__item">
                    <h4>Default</h4>
                    <ESSCheckbox label="Subscribe to newsletter" />
                  </div>
                  <div className="components-showcase__item">
                    <h4>With Description</h4>
                    <ESSCheckbox
                      label="Accept terms"
                      description="I agree to the terms and conditions"
                    />
                  </div>
                </div>
              </div>

              {/* Switch */}
              <div className="components-showcase__subsection">
                <h3 className="components-showcase__subsection-title">Switch</h3>
                <div className="components-showcase__grid components-showcase__grid--form">
                  <div className="components-showcase__item">
                    <h4>Default</h4>
                    <ESSSwitch label="Enable notifications" />
                  </div>
                  <div className="components-showcase__item">
                    <h4>With Description</h4>
                    <ESSSwitch
                      label="Dark Mode"
                      description="Switch to dark theme"
                    />
                  </div>
                </div>
              </div>

              {/* RadioGroup */}
              <div className="components-showcase__subsection">
                <h3 className="components-showcase__subsection-title">
                  RadioGroup
                </h3>
                <div className="components-showcase__grid components-showcase__grid--form">
                  <div className="components-showcase__item">
                    <h4>Vertical</h4>
                    <ESSRadioGroup
                      label="Subscription Plan"
                      name="plan-vertical"
                      options={[
                        { value: 'free', label: 'Free' },
                        { value: 'pro', label: 'Pro' },
                        { value: 'enterprise', label: 'Enterprise' },
                      ]}
                    />
                  </div>
                  <div className="components-showcase__item">
                    <h4>Horizontal</h4>
                    <ESSRadioGroup
                      label="Size"
                      name="size-horizontal"
                      orientation="horizontal"
                      options={[
                        { value: 's', label: 'S' },
                        { value: 'm', label: 'M' },
                        { value: 'l', label: 'L' },
                        { value: 'xl', label: 'XL' },
                      ]}
                    />
                  </div>
                </div>
              </div>

              {/* ComboBox */}
              <div className="components-showcase__subsection">
                <h3 className="components-showcase__subsection-title">ComboBox</h3>
                <div className="components-showcase__grid components-showcase__grid--form">
                  <div className="components-showcase__item">
                    <h4>Default</h4>
                    <ESSComboBox
                      label="Search City"
                      placeholder="Type to search..."
                      options={['New York', 'London', 'Paris', 'Tokyo', 'Berlin']}
                    />
                  </div>
                </div>
              </div>

              {/* DatePicker */}
              <div className="components-showcase__subsection">
                <h3 className="components-showcase__subsection-title">
                  DatePicker
                </h3>
                <div className="components-showcase__grid components-showcase__grid--form">
                  <div className="components-showcase__item">
                    <h4>Default</h4>
                    <ESSDatePicker label="Birth Date" />
                  </div>
                  <div className="components-showcase__item">
                    <h4>With Helper Text</h4>
                    <ESSDatePicker
                      label="Event Date"
                      helperText="Select the date for your event"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* ========================================
                MOLECULES (CUSTOM COMPONENTS)
                Unique ESS components
            ======================================== */}

            {/* Logo Component */}
            <section className="components-showcase__section">
              <h2 className="components-showcase__section-title">
                Logo – Brand Identity
              </h2>
              <p className="components-showcase__description">
                Flexible logo component supporting image URLs and inline SVG with
                customizable colors. Responsive height: mobile 45px, desktop 55px
                (configurable).
              </p>
              <div className="components-showcase__grid components-showcase__grid--form">
                <div className="components-showcase__item">
                  <h3>Image Variant (JPG)</h3>
                  <div className="logo-preview">
                    <Logo
                      src={BscLogoJpg}
                      mobileHeight="40px"
                      desktopHeight="60px"
                      alt="BSC Logo JPG"
                    />
                  </div>
                </div>
                <div className="components-showcase__item">
                  <h3>Image Variant (SVG file)</h3>
                  <div className="logo-preview">
                    <Logo
                      src={BscLogoSvg}
                      mobileHeight="40px"
                      desktopHeight="60px"
                      alt="BSC Logo SVG"
                    />
                  </div>
                </div>
                <div className="components-showcase__item">
                  <h3>Inline SVG with Custom Color</h3>
                  <div
                    className="logo-preview"
                    style={{ background: '#f5f5f5', padding: '1rem' }}
                  >
                    <Logo
                      svg={
                        <svg
                          viewBox="0 0 100 40"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect x="5" y="5" width="30" height="30" rx="4" />
                          <text x="45" y="28" fontSize="20" fontWeight="bold">
                            ESS
                          </text>
                        </svg>
                      }
                      color="#0ff"
                      hoverColor="#00f"
                      mobileHeight="40px"
                      desktopHeight="50px"
                      alt="ESS Logo Blue"
                    />
                  </div>
                </div>
                <div className="components-showcase__item">
                  <h3>Inline SVG - Brand Colors</h3>
                  <div className="logo-preview">
                    <Logo
                      svg={
                        <svg
                          version="1.1"
                          id="Capa_1"
                          xmlns="http://www.w3.org/2000/svg"
                          x="0px"
                          y="0px"
                          viewBox="0 0 283.46 69.36"
                          enable-background="new 0 0 283.46 69.36"
                        >
                          <g id="BSC_Logo_2_">
                            <g>
                              <g>
                                <path
                                  fill="#213970"
                                  d="M91.2,9.25h4.99c2.11,0,2.9,0.74,2.9,2.14c0,1.22-0.61,2.03-1.78,2.34H97.3v0.03
            c0.9,0.19,1.47,1.05,1.47,1.95c0,2.34-1.88,3.27-4,3.27h-5.58L91.2,9.25z M92.59,16.65h1.92c0.53,0,1.27-0.19,1.27-0.98
            c0-0.72-0.53-0.83-1.16-0.83h-1.65L92.59,16.65z M93.34,13.03h1.68c0.48,0,1.07-0.18,1.07-0.78c0-0.59-0.39-0.69-0.92-0.69h-1.54
            L93.34,13.03z"
                                />
                                <path
                                  fill="#213970"
                                  d="M100.23,14.04c0.42-1.88,1.88-2.31,3.61-2.31c1.28,0,3.43,0.16,3.43,1.88c0,1.1-0.86,4.27-0.86,4.74
            c0.05,0.54,0,0.3,0.11,0.63h-2.64c-0.07-0.22-0.11-0.45-0.1-0.68h-0.03c-0.48,0.57-1.39,0.86-2.18,0.86
            c-1.21,0-2.29-0.53-2.29-1.88c0-1.87,1.39-2.37,3.41-2.52c0.76-0.06,1.98,0.05,1.98-0.82c0-0.49-0.44-0.61-0.84-0.61
            c-0.54,0-0.93,0.19-1.07,0.72H100.23z M102.84,17.55c0.91,0,1.28-0.67,1.44-1.72c-0.44,0.22-0.94,0.27-1.43,0.31
            c-0.45,0.04-0.87,0.27-0.87,0.79C101.98,17.43,102.4,17.55,102.84,17.55z"
                                />
                                <path
                                  fill="#213970"
                                  d="M109.24,11.9h2.61l-0.23,1.1h0.03c0.47-0.75,1.36-1.28,2.05-1.28c0.45,0,0.71,0.03,0.95,0.11l-0.5,2.38
            c-0.33-0.12-0.69-0.18-1.04-0.18c-1.14,0-1.71,0.49-2.03,1.97l-0.61,2.95h-2.7L109.24,11.9z"
                                />
                                <path
                                  fill="#213970"
                                  d="M119.25,14.58c0.01-0.34-0.04-0.56-0.19-0.69c-0.14-0.15-0.37-0.2-0.68-0.2c-1.17,0-1.48,1.54-1.48,2.21
            c0,0.67,0.15,1.29,0.92,1.29c0.74,0,1.03-0.64,1.18-1.04h2.59c-0.46,2.01-1.76,2.99-3.8,2.99c-2.99,0-3.59-1.91-3.59-3.23
            c0-2.41,1.57-4.19,4.26-4.19c1.96,0,3.42,0.88,3.38,2.86H119.25z"
                                />
                                <path
                                  fill="#213970"
                                  d="M129.54,16.91c-0.18,0.48-0.71,2.23-3.76,2.23c-2.5,0-3.51-1.4-3.51-3.12c0-3.23,2.48-4.3,4.23-4.3
            c1.57,0,3.46,0.87,3.46,3.09c0,0.38-0.01,0.79-0.14,1.22h-5.03c0,0.75,0.26,1.37,1.1,1.37c0.59,0,0.92-0.26,1.06-0.5H129.54
            L129.54,16.91z M127.42,14.64c0.01-0.07,0.03-0.16,0.03-0.23c0-0.53-0.34-0.95-1.02-0.95c-0.72,0-1.2,0.44-1.4,1.18H127.42
            L127.42,14.64z"
                                />
                                <polygon
                                  fill="#213970"
                                  points="132.55,9.25 135.24,9.25 133.23,18.97 130.53,18.97 			"
                                />
                                <path
                                  fill="#213970"
                                  d="M139.49,11.73c2.07,0,3.43,0.96,3.43,3.09c0,1.81-1.01,4.33-4.26,4.33c-1.95,0-3.53-0.94-3.53-3.31
            C135.13,13.58,136.6,11.73,139.49,11.73z M138.78,17.19c1.06,0,1.44-1.57,1.44-2.18c0-0.84-0.22-1.32-0.94-1.32
            c-1.08,0-1.46,1.62-1.46,2.36C137.83,16.61,138.02,17.19,138.78,17.19z"
                                />
                                <path
                                  fill="#213970"
                                  d="M144.79,11.9h2.6l-0.2,0.9h0.03c0.52-0.64,1.34-1.07,2.19-1.07c1.24,0,2.3,0.52,2.3,1.9
            c0,0.31-0.08,0.83-0.19,1.35l-0.86,3.99h-2.7l0.69-3.24c0.08-0.41,0.22-0.89,0.22-1.17c0-0.45-0.33-0.68-0.76-0.68
            c-0.86,0-1.14,0.61-1.31,1.35l-0.79,3.74h-2.7L144.79,11.9z"
                                />
                                <path
                                  fill="#213970"
                                  d="M153.17,14.04c0.42-1.88,1.88-2.31,3.61-2.31c1.28,0,3.43,0.16,3.43,1.88c0,1.1-0.86,4.27-0.86,4.74
            c0.05,0.54,0,0.3,0.11,0.63h-2.64c-0.07-0.22-0.11-0.45-0.09-0.68h-0.03c-0.47,0.57-1.39,0.86-2.18,0.86
            c-1.21,0-2.29-0.53-2.29-1.88c0-1.87,1.39-2.37,3.42-2.52c0.76-0.06,1.97,0.05,1.97-0.82c0-0.49-0.43-0.61-0.84-0.61
            c-0.54,0-0.93,0.19-1.07,0.72H153.17z M155.78,17.55c0.91,0,1.28-0.67,1.44-1.72c-0.44,0.22-0.94,0.27-1.43,0.31
            c-0.45,0.04-0.87,0.27-0.87,0.79C154.92,17.43,155.34,17.55,155.78,17.55z"
                                />
                                <path
                                  fill="#213970"
                                  d="M95.49,27.8c0.01-0.39-0.12-0.64-0.34-0.8c-0.22-0.16-0.53-0.22-0.87-0.22c-0.5,0-1.16,0.15-1.16,0.79
            c0,0.73,1.46,0.77,2.41,1.11c1.69,0.6,2.26,1.52,2.26,2.68c0,2.34-2.27,3.44-4.38,3.44c-2.23,0-4.25-1.02-4.12-3.5h2.9
            c0,0.53,0.12,0.86,0.36,1.1c0.26,0.22,0.61,0.25,1.11,0.25c0.58,0,1.3-0.3,1.3-0.94c0-0.71-0.99-0.8-2.34-1.25
            c-1.18-0.39-2.33-1.02-2.33-2.64c0-2.19,2.13-3.2,4.08-3.2c2.07,0,3.93,0.78,3.88,3.17H95.49L95.49,27.8z"
                                />
                                <path
                                  fill="#213970"
                                  d="M105.48,34.58h-2.61l0.22-0.9h-0.03c-0.58,0.7-1.36,1.08-2.27,1.08c-1.22,0-2.24-0.53-2.24-1.88
            c0-0.61,0.23-1.47,1.05-5.36h2.7l-0.79,3.69c-0.11,0.37-0.11,0.74-0.11,0.74c0,0.43,0.38,0.67,0.78,0.67
            c1.21,0,1.27-1.32,1.48-2.22l0.61-2.87h2.69L105.48,34.58z"
                                />
                                <path
                                  fill="#213970"
                                  d="M108.5,27.51h2.61l-0.16,0.8h0.03c0.46-0.65,1.06-0.98,1.89-0.98c1.99,0,2.57,1.65,2.57,2.9
            c0,2.1-1.05,4.52-3.44,4.52c-0.77,0-1.54-0.1-2.12-1.03l-0.64,3.14h-2.7L108.5,27.51z M112.75,30.55c0-0.69-0.19-1.25-0.98-1.25
            c-1.2,0-1.42,1.71-1.42,2.34c0,0.26,0.1,1.16,0.98,1.16C112.41,32.79,112.75,31.46,112.75,30.55z"
                                />
                                <path
                                  fill="#213970"
                                  d="M123.48,32.52c-0.18,0.48-0.71,2.23-3.76,2.23c-2.5,0-3.51-1.4-3.51-3.12c0-3.24,2.48-4.3,4.23-4.3
            c1.57,0,3.46,0.87,3.46,3.09c0,0.38-0.01,0.79-0.14,1.22h-5.03c0,0.75,0.26,1.37,1.1,1.37c0.59,0,0.92-0.26,1.06-0.5H123.48z
            M121.36,30.25c0.02-0.07,0.03-0.16,0.03-0.23c0-0.53-0.34-0.95-1.02-0.95c-0.72,0-1.2,0.44-1.4,1.18H121.36z"
                                />
                                <path
                                  fill="#213970"
                                  d="M125.87,27.51h2.61l-0.23,1.1h0.03c0.48-0.75,1.36-1.28,2.06-1.28c0.45,0,0.71,0.03,0.95,0.11l-0.51,2.38
            c-0.33-0.12-0.69-0.18-1.03-0.18c-1.14,0-1.72,0.49-2.03,1.97l-0.61,2.95h-2.7L125.87,27.51z"
                                />
                                <path
                                  fill="#213970"
                                  d="M135.88,30.2c0.02-0.34-0.04-0.56-0.19-0.69c-0.13-0.15-0.36-0.2-0.68-0.2c-1.17,0-1.48,1.54-1.48,2.2
            s0.15,1.29,0.93,1.29c0.74,0,1.03-0.64,1.18-1.03h2.59c-0.46,2.01-1.75,2.99-3.8,2.99c-2.99,0-3.59-1.91-3.59-3.22
            c0-2.41,1.57-4.19,4.26-4.19c1.96,0,3.41,0.89,3.37,2.86H135.88z"
                                />
                                <path
                                  fill="#213970"
                                  d="M143.26,27.34c2.07,0,3.43,0.95,3.43,3.09c0,1.81-1.01,4.33-4.26,4.33c-1.95,0-3.52-0.94-3.52-3.31
            C138.9,29.19,140.37,27.34,143.26,27.34z M142.55,32.79c1.06,0,1.44-1.57,1.44-2.18c0-0.84-0.22-1.32-0.94-1.32
            c-1.08,0-1.46,1.62-1.46,2.36C141.6,32.22,141.79,32.79,142.55,32.79z"
                                />
                                <path
                                  fill="#213970"
                                  d="M148.57,27.51h2.61l-0.19,0.92h0.03c0.54-0.66,1.32-1.1,2.22-1.1c0.92,0,1.73,0.3,1.9,1.17
            c0.59-0.73,1.38-1.17,2.31-1.17c1.37,0,2.26,0.64,2.26,2.1c0,0.33-0.11,0.8-0.16,1.12l-0.86,4.03H156l0.72-3.38
            c0.02-0.14,0.16-0.83,0.16-1.03c0-0.45-0.26-0.68-0.69-0.68c-0.53,0-0.92,0.3-1.02,0.82l-0.92,4.27h-2.69l0.82-3.78
            c0.04-0.18,0.11-0.47,0.11-0.66c0-0.45-0.25-0.65-0.68-0.65c-0.67,0-1.01,0.43-1.13,1.05l-0.87,4.04h-2.69L148.57,27.51z"
                                />
                                <path
                                  fill="#213970"
                                  d="M161.68,27.51h2.62l-0.17,0.8h0.03c0.46-0.65,1.06-0.98,1.89-0.98c1.99,0,2.57,1.65,2.57,2.9
            c0,2.1-1.05,4.52-3.44,4.52c-0.78,0-1.54-0.1-2.13-1.03l-0.64,3.14h-2.7L161.68,27.51z M165.93,30.55c0-0.69-0.19-1.25-0.98-1.25
            c-1.2,0-1.42,1.71-1.42,2.34c0,0.26,0.1,1.16,0.98,1.16C165.59,32.79,165.93,31.46,165.93,30.55z"
                                />
                                <path
                                  fill="#213970"
                                  d="M176.3,34.58h-2.61l0.22-0.9h-0.03c-0.59,0.7-1.36,1.08-2.27,1.08c-1.22,0-2.24-0.53-2.24-1.88
            c0-0.61,0.23-1.47,1.05-5.36h2.7l-0.79,3.69c-0.11,0.37-0.11,0.74-0.11,0.74c0,0.43,0.38,0.67,0.78,0.67
            c1.21,0,1.27-1.32,1.48-2.22l0.61-2.87h2.69L176.3,34.58z"
                                />
                                <path
                                  fill="#213970"
                                  d="M182.41,27.51h1.46l-0.38,1.66h-1.43l-0.48,2.27c-0.05,0.23-0.12,0.48-0.12,0.71
            c0,0.23,0.12,0.42,0.55,0.42c0.44,0,0.67-0.03,0.89-0.04l-0.41,1.95c-0.48,0.08-0.97,0.15-1.46,0.18c-0.57,0-1.19,0-1.7-0.29
            c-0.46-0.26-0.73-0.82-0.73-1.33c0-0.38,0.08-0.64,0.15-0.97l0.63-2.9h-1.2l0.38-1.66h1.17l0.47-2.14h2.69L182.41,27.51z"
                                />
                                <path
                                  fill="#213970"
                                  d="M184.92,27.51h2.7l-1.47,7.06h-2.69L184.92,27.51z M187.76,26.75h-2.7l0.39-1.89h2.7L187.76,26.75z"
                                />
                                <path
                                  fill="#213970"
                                  d="M189.14,27.51h2.6l-0.2,0.9h0.03c0.51-0.64,1.33-1.07,2.19-1.07c1.24,0,2.3,0.52,2.3,1.91
            c0,0.31-0.08,0.83-0.19,1.35L195,34.58h-2.69l0.69-3.24c0.08-0.41,0.22-0.89,0.22-1.17c0-0.45-0.33-0.68-0.76-0.68
            c-0.86,0-1.14,0.61-1.31,1.35l-0.79,3.74h-2.7L189.14,27.51z"
                                />
                                <path
                                  fill="#213970"
                                  d="M203.85,33.35c-0.41,1.99-0.78,3.69-4.19,3.69c-0.83,0-3.36-0.09-3.27-2.36h2.67
            c0,0.28,0.07,0.46,0.22,0.61c0.14,0.11,0.35,0.15,0.63,0.15c0.82,0,1.02-0.63,1.16-1.25l0.17-0.75h-0.03
            c-0.41,0.46-1.03,0.71-1.68,0.71c-1.69,0-2.76-1.07-2.76-2.75c0-2.02,1.2-4.07,3.4-4.07c0.87,0,1.68,0.26,2.1,1.09h0.03
            l0.18-0.91h2.6L203.85,33.35z M199.44,31.3c0,0.63,0.31,1.13,0.92,1.13c1.28,0,1.5-1.55,1.5-2.03c0-0.43-0.04-1.1-1.01-1.1
            C199.79,29.3,199.44,30.47,199.44,31.3z"
                                />
                                <path
                                  fill="#213970"
                                  d="M99,46.5c-0.18,0.99-0.94,3.92-4.98,3.92c-2.82,0-4.18-1.83-4.18-4.42c0-3.15,1.96-5.76,5.27-5.76
            c2.16,0,4.09,0.94,4.15,3.85h-2.83c0.03-0.91-0.41-1.43-1.34-1.43c-1.66,0-2.26,1.91-2.26,3.28c0,0.8,0.19,2.06,1.57,2.06
            c0.94,0,1.47-0.64,1.66-1.5H99L99,46.5z"
                                />
                                <path
                                  fill="#213970"
                                  d="M107.1,48.13c-0.18,0.48-0.71,2.23-3.76,2.23c-2.5,0-3.51-1.4-3.51-3.12c0-3.24,2.48-4.3,4.23-4.3
            c1.57,0,3.46,0.87,3.46,3.09c0,0.38-0.01,0.79-0.14,1.22h-5.03c0,0.75,0.26,1.37,1.1,1.37c0.59,0,0.92-0.26,1.06-0.5H107.1z
            M104.98,45.86c0.01-0.07,0.03-0.16,0.03-0.23c0-0.53-0.34-0.95-1.02-0.95c-0.72,0-1.2,0.44-1.4,1.18H104.98z"
                                />
                                <path
                                  fill="#213970"
                                  d="M109.49,43.12h2.6l-0.2,0.9h0.03c0.51-0.64,1.33-1.07,2.19-1.07c1.24,0,2.3,0.52,2.3,1.9
            c0,0.31-0.08,0.83-0.19,1.35l-0.86,3.99h-2.7l0.69-3.24c0.09-0.41,0.22-0.89,0.22-1.17c0-0.45-0.33-0.68-0.76-0.68
            c-0.86,0-1.14,0.61-1.31,1.35l-0.79,3.74h-2.7L109.49,43.12z"
                                />
                                <path
                                  fill="#213970"
                                  d="M121.41,43.12h1.46l-0.38,1.66h-1.43l-0.48,2.27c-0.05,0.23-0.12,0.48-0.12,0.71
            c0,0.23,0.12,0.42,0.54,0.42c0.44,0,0.67-0.03,0.89-0.04l-0.41,1.94c-0.48,0.08-0.97,0.15-1.46,0.18c-0.57,0-1.18,0-1.7-0.29
            c-0.46-0.26-0.74-0.81-0.74-1.33c0-0.38,0.08-0.64,0.15-0.97l0.63-2.9h-1.2l0.38-1.66h1.17l0.48-2.13h2.69L121.41,43.12z"
                                />
                                <path
                                  fill="#213970"
                                  d="M130.03,48.13c-0.18,0.48-0.71,2.23-3.76,2.23c-2.5,0-3.51-1.4-3.51-3.12c0-3.24,2.48-4.3,4.23-4.3
            c1.57,0,3.46,0.87,3.46,3.09c0,0.38-0.01,0.79-0.13,1.22h-5.04c0,0.75,0.26,1.37,1.11,1.37c0.58,0,0.92-0.26,1.06-0.5H130.03z
            M127.91,45.86c0.01-0.07,0.03-0.16,0.03-0.23c0-0.53-0.34-0.95-1.02-0.95c-0.72,0-1.2,0.44-1.4,1.18H127.91z"
                                />
                                <path
                                  fill="#213970"
                                  d="M132.41,43.12h2.61l-0.23,1.1h0.03c0.48-0.75,1.36-1.28,2.06-1.28c0.45,0,0.71,0.03,0.95,0.11l-0.51,2.38
            c-0.33-0.12-0.69-0.18-1.03-0.18c-1.14,0-1.72,0.49-2.03,1.97l-0.61,2.95h-2.69L132.41,43.12z"
                                />
                                <path
                                  fill="#213970"
                                  d="M96.76,60.48c-0.4,1.79-1.57,2.89-3.54,2.89c-2.18,0-3.43-1.3-3.43-3.51c0-2.59,1.66-4.87,4.39-4.87
            c1.73,0,3.05,0.92,3.05,2.77h-1.07c0-1.26-0.82-1.87-1.98-1.87c-2.23,0-3.33,2.05-3.33,3.98c0,1.55,0.76,2.61,2.37,2.61
            c1.31,0,2.13-0.75,2.48-2H96.76L96.76,60.48z"
                                />
                                <path
                                  fill="#213970"
                                  d="M98.55,60.57c-0.02,0.17-0.03,0.34-0.03,0.51c0,0.9,0.73,1.4,1.5,1.4c0.83,0,1.4-0.39,1.67-1.11h0.95
            c-0.3,1.28-1.36,1.95-2.62,1.95c-1.92,0-2.45-1.32-2.45-2.33c0-2.22,1.39-3.73,2.98-3.73c1.7,0,2.45,0.93,2.45,2.57
            c0,0.3-0.05,0.59-0.09,0.75L98.55,60.57L98.55,60.57z M102.05,59.73c0.07-0.82-0.3-1.64-1.35-1.64c-1.12,0-1.73,0.74-2.01,1.64
            H102.05z"
                                />
                                <path
                                  fill="#213970"
                                  d="M104.64,57.39h0.92l-0.18,0.87h0.02c0.48-0.65,1.27-1.01,2.06-1.01c1.09,0,1.61,0.47,1.61,1.49
            c0,0.25-0.07,0.69-0.17,1.13l-0.71,3.3h-0.95l0.74-3.38c0.04-0.21,0.13-0.69,0.13-0.9c0-0.55-0.45-0.81-1.01-0.81
            c-0.53,0-1.08,0.29-1.43,0.67c-0.34,0.29-0.51,0.75-0.64,1.21l-0.67,3.2h-0.95L104.64,57.39z"
                                />
                                <path
                                  fill="#213970"
                                  d="M112.18,57.39h1.15l-0.18,0.84H112l-0.72,3.4c-0.01,0.14-0.03,0.25-0.03,0.38c0,0.31,0.28,0.39,0.57,0.39
            c0.19,0,0.37-0.05,0.55-0.1l-0.17,0.87c-0.22,0.05-0.45,0.07-0.67,0.07c-0.71,0-1.3-0.2-1.3-0.94c0-0.17,0.02-0.3,0.05-0.48
            l0.76-3.6h-0.97l0.18-0.84h0.97l0.36-1.72h0.95L112.18,57.39z"
                                />
                                <path
                                  fill="#213970"
                                  d="M114.39,57.39h0.9l-0.28,1.24h0.02c0.45-0.79,1.13-1.38,2.1-1.38c0.11,0,0.23-0.02,0.34,0l-0.22,1.02
            c-0.09-0.01-0.19-0.01-0.28-0.01c-0.23,0-0.35,0-0.57,0.05c-0.53,0.13-0.93,0.48-1.23,0.94c-0.26,0.37-0.39,0.92-0.48,1.36
            l-0.55,2.56h-0.95L114.39,57.39z"
                                />
                                <path
                                  fill="#213970"
                                  d="M120.4,57.25c1.55,0,2.41,0.85,2.41,2.4c0,1.9-1.18,3.67-3.21,3.67c-1.52,0-2.44-1.02-2.44-2.42
            C117.16,59,118.37,57.25,120.4,57.25z M119.7,62.48c1.39,0,2.15-1.6,2.15-2.75c0-0.91-0.48-1.64-1.53-1.64
            c-1.46,0-2.22,1.59-2.22,2.79C118.11,61.84,118.66,62.48,119.7,62.48z"
                                />
                                <polygon
                                  fill="#213970"
                                  points="128.29,55.18 129.43,55.18 132.29,61.8 132.31,61.8 133.65,55.18 134.68,55.18 132.99,63.18 
            131.84,63.18 129.01,56.6 128.98,56.6 127.63,63.18 126.6,63.18 			"
                                />
                                <path
                                  fill="#213970"
                                  d="M135.2,59.13c0.19-1.29,1.28-1.88,2.51-1.88c1.12,0,2.12,0.3,2.12,1.48c0,0.33-0.11,0.74-0.18,1.05
            l-0.37,1.58c-0.05,0.23-0.16,0.56-0.16,0.83c0,0.27,0.12,0.36,0.28,0.36c0.09,0,0.21-0.05,0.31-0.08l-0.15,0.7
            c-0.15,0.08-0.41,0.13-0.58,0.13c-0.53,0-0.81-0.31-0.75-0.81l-0.02-0.02c-0.4,0.53-0.96,0.84-1.94,0.84
            c-0.96,0-1.77-0.45-1.77-1.63c0-1.68,1.65-1.79,2.92-1.9c1.01-0.09,1.46-0.15,1.46-0.92c0-0.63-0.66-0.76-1.17-0.76
            c-0.73,0-1.45,0.29-1.56,1.04H135.2L135.2,59.13z M136.49,62.48c0.74,0,1.29-0.25,1.63-0.76c0.31-0.44,0.37-0.95,0.5-1.49h-0.02
            c-0.41,0.25-1.17,0.3-1.86,0.39c-0.68,0.1-1.29,0.31-1.29,1.05C135.45,62.21,135.95,62.48,136.49,62.48z"
                                />
                                <path
                                  fill="#213970"
                                  d="M145.09,59.23c0-0.71-0.48-1.13-1.22-1.13c-1.47,0-2.25,1.46-2.25,2.72c0,0.9,0.37,1.66,1.44,1.66
            c0.82,0,1.42-0.48,1.69-1.19h0.96c-0.34,1.29-1.31,2.03-2.64,2.03c-1.58,0-2.39-0.94-2.39-2.5c0-1.84,1.13-3.56,3.2-3.56
            c1.31,0,2.13,0.63,2.17,1.97H145.09z"
                                />
                                <path
                                  fill="#213970"
                                  d="M147.6,57.39h0.96l-1.21,5.79h-0.95L147.6,57.39z M148.78,56.35h-0.97l0.25-1.17h0.96L148.78,56.35z"
                                />
                                <path
                                  fill="#213970"
                                  d="M152.36,57.25c1.54,0,2.41,0.85,2.41,2.4c0,1.9-1.18,3.67-3.2,3.67c-1.53,0-2.44-1.02-2.44-2.42
            C149.12,59,150.33,57.25,152.36,57.25z M151.67,62.48c1.39,0,2.15-1.6,2.15-2.75c0-0.91-0.48-1.64-1.53-1.64
            c-1.46,0-2.22,1.59-2.22,2.79C150.08,61.84,150.63,62.48,151.67,62.48z"
                                />
                                <path
                                  fill="#213970"
                                  d="M156.53,57.39h0.92l-0.18,0.87h0.02c0.48-0.65,1.27-1.01,2.06-1.01c1.09,0,1.61,0.47,1.61,1.49
            c0,0.25-0.07,0.69-0.17,1.13l-0.7,3.3h-0.95l0.74-3.38c0.04-0.21,0.13-0.69,0.13-0.9c0-0.55-0.45-0.81-1.01-0.81
            c-0.53,0-1.08,0.29-1.43,0.67c-0.34,0.29-0.51,0.75-0.64,1.21l-0.67,3.2h-0.95L156.53,57.39z"
                                />
                                <path
                                  fill="#213970"
                                  d="M162.19,59.13c0.19-1.29,1.28-1.88,2.51-1.88c1.12,0,2.12,0.3,2.12,1.48c0,0.33-0.11,0.74-0.18,1.05
            l-0.37,1.58c-0.05,0.23-0.16,0.56-0.16,0.83c0,0.27,0.13,0.36,0.28,0.36c0.09,0,0.21-0.05,0.3-0.08l-0.15,0.7
            c-0.14,0.08-0.41,0.13-0.58,0.13c-0.53,0-0.81-0.31-0.75-0.81l-0.02-0.02c-0.41,0.53-0.97,0.84-1.94,0.84
            c-0.97,0-1.77-0.45-1.77-1.63c0-1.68,1.65-1.79,2.92-1.9c1.01-0.09,1.46-0.15,1.46-0.92c0-0.63-0.66-0.76-1.17-0.76
            c-0.73,0-1.44,0.29-1.56,1.04H162.19L162.19,59.13z M163.48,62.48c0.74,0,1.29-0.25,1.63-0.76c0.31-0.44,0.37-0.95,0.51-1.49
            h-0.02c-0.41,0.25-1.17,0.3-1.86,0.39c-0.68,0.1-1.29,0.31-1.29,1.05C162.44,62.21,162.94,62.48,163.48,62.48z"
                                />
                                <polygon
                                  fill="#213970"
                                  points="169.04,55.18 169.99,55.18 168.31,63.18 167.36,63.18 			"
                                />
                                <path
                                  fill="#213970"
                                  d="M178.12,63.18h-0.9l0.2-0.83h-0.02c-0.57,0.75-1.14,0.96-1.92,0.96c-1.48,0-2.28-0.96-2.28-2.37
            c0-1.76,1.19-3.69,3.22-3.69c0.71,0,1.58,0.25,1.8,1.02h0.02l0.61-3.09h0.94L178.12,63.18z M175.65,62.48
            c1.45,0,2.26-1.67,2.26-2.88c0-0.86-0.51-1.5-1.46-1.5c-1.51,0-2.3,1.57-2.3,2.85C174.16,61.88,174.61,62.48,175.65,62.48z"
                                />
                                <path
                                  fill="#213970"
                                  d="M180.77,60.57c-0.02,0.17-0.04,0.34-0.04,0.51c0,0.9,0.73,1.4,1.5,1.4c0.83,0,1.4-0.39,1.67-1.11h0.95
            c-0.3,1.28-1.36,1.95-2.62,1.95c-1.92,0-2.45-1.32-2.45-2.33c0-2.22,1.39-3.73,2.98-3.73c1.7,0,2.45,0.93,2.45,2.57
            c0,0.3-0.05,0.59-0.09,0.75L180.77,60.57L180.77,60.57z M184.26,59.73c0.06-0.82-0.3-1.64-1.36-1.64c-1.12,0-1.73,0.74-2.01,1.64
            H184.26z"
                                />
                                <path
                                  fill="#213970"
                                  d="M194.62,57.55c0.03-1.27-0.74-1.66-1.84-1.66c-0.79,0-1.85,0.36-1.85,1.31c0,0.77,0.57,1.09,1.3,1.29
            l1.08,0.29c0.99,0.27,1.93,0.73,1.93,2.16c0,1.65-1.67,2.43-3.11,2.43c-1.93,0-3.28-0.85-3.15-2.81h1.07
            c-0.07,1.45,0.83,1.92,2.13,1.92c1,0,2.01-0.46,2.01-1.53c0-0.82-0.64-1.11-1.42-1.32l-1.09-0.29c-0.96-0.26-1.79-0.84-1.79-2.01
            c0-1.55,1.49-2.32,2.91-2.32c1.58,0,2.96,0.77,2.9,2.55H194.62L194.62,57.55z"
                                />
                                <path
                                  fill="#213970"
                                  d="M200.67,63.18h-0.92l0.18-0.87h-0.02c-0.48,0.65-1.27,1.01-2.06,1.01c-1.09,0-1.61-0.47-1.61-1.49
            c0-0.25,0.07-0.69,0.17-1.13l0.71-3.3h0.95l-0.74,3.38c-0.05,0.21-0.13,0.7-0.13,0.9c0,0.55,0.45,0.81,1.01,0.81
            c1.05,0,1.87-0.92,2.07-1.88l0.67-3.2h0.95L200.67,63.18z"
                                />
                                <path
                                  fill="#213970"
                                  d="M203.44,57.39h0.89l-0.2,0.83h0.02c0.57-0.75,1.14-0.96,1.92-0.96c1.48,0,2.28,0.96,2.28,2.37
            c0,1.76-1.19,3.69-3.22,3.69c-0.71,0-1.58-0.25-1.8-1.02h-0.02l-0.61,3.1h-0.94L203.44,57.39z M205.9,58.09
            c-1.45,0-2.26,1.67-2.26,2.88c0,0.86,0.51,1.5,1.46,1.5c1.51,0,2.3-1.57,2.3-2.85C207.39,58.69,206.95,58.09,205.9,58.09z"
                                />
                                <path
                                  fill="#213970"
                                  d="M210.04,60.57c-0.02,0.17-0.03,0.34-0.03,0.51c0,0.9,0.73,1.4,1.5,1.4c0.83,0,1.4-0.39,1.67-1.11h0.95
            c-0.3,1.28-1.36,1.95-2.62,1.95c-1.92,0-2.45-1.32-2.45-2.33c0-2.22,1.39-3.73,2.98-3.73c1.7,0,2.45,0.93,2.45,2.57
            c0,0.3-0.05,0.59-0.09,0.75L210.04,60.57L210.04,60.57z M213.54,59.73c0.07-0.82-0.3-1.64-1.36-1.64c-1.12,0-1.73,0.74-2.01,1.64
            H213.54z"
                                />
                                <path
                                  fill="#213970"
                                  d="M216.11,57.39h0.9l-0.28,1.24h0.02c0.45-0.79,1.13-1.38,2.11-1.38c0.11,0,0.23-0.02,0.34,0l-0.22,1.02
            c-0.09-0.01-0.19-0.01-0.28-0.01c-0.23,0-0.35,0-0.58,0.05c-0.52,0.13-0.93,0.48-1.23,0.94c-0.25,0.37-0.39,0.92-0.48,1.36
            l-0.55,2.56h-0.95L216.11,57.39z"
                                />
                                <path
                                  fill="#213970"
                                  d="M223.35,59.23c0-0.71-0.48-1.13-1.22-1.13c-1.47,0-2.25,1.46-2.25,2.72c0,0.9,0.37,1.66,1.43,1.66
            c0.82,0,1.42-0.48,1.69-1.19h0.95c-0.33,1.29-1.31,2.03-2.64,2.03c-1.58,0-2.38-0.94-2.38-2.5c0-1.84,1.13-3.56,3.21-3.56
            c1.31,0,2.13,0.63,2.17,1.97H223.35z"
                                />
                                <path
                                  fill="#213970"
                                  d="M228.14,57.25c1.55,0,2.41,0.85,2.41,2.4c0,1.9-1.18,3.67-3.2,3.67c-1.53,0-2.44-1.02-2.44-2.42
            C224.9,59,226.11,57.25,228.14,57.25z M227.44,62.48c1.39,0,2.15-1.6,2.15-2.75c0-0.91-0.48-1.64-1.52-1.64
            c-1.46,0-2.22,1.59-2.22,2.79C225.86,61.84,226.4,62.48,227.44,62.48z"
                                />
                                <path
                                  fill="#213970"
                                  d="M232.32,57.39h0.89l-0.21,0.86l0.02,0.02c0.49-0.6,1.22-1.02,2.02-1.02c0.73,0,1.29,0.29,1.46,1.04
            c0.45-0.62,1.21-1.04,1.98-1.04c0.82,0,1.61,0.36,1.61,1.3c0,0.33-0.09,0.83-0.17,1.15l-0.73,3.47h-0.95l0.81-3.67
            c0.05-0.18,0.09-0.46,0.09-0.63c0-0.45-0.38-0.79-0.88-0.79c-0.54,0-1.13,0.38-1.46,0.79c-0.31,0.34-0.44,0.81-0.54,1.3
            l-0.64,2.99h-0.95l0.7-3.31c0.1-0.45,0.18-0.89,0.18-1.02c0-0.48-0.29-0.76-0.81-0.76c-0.53,0-1.09,0.36-1.42,0.74
            c-0.4,0.39-0.49,0.74-0.62,1.31l-0.65,3.04h-0.95L232.32,57.39z"
                                />
                                <path
                                  fill="#213970"
                                  d="M241.84,57.39h0.9l-0.2,0.83h0.02c0.57-0.75,1.14-0.96,1.92-0.96c1.48,0,2.28,0.96,2.28,2.37
            c0,1.76-1.19,3.69-3.22,3.69c-0.71,0-1.58-0.25-1.8-1.02h-0.03l-0.61,3.1h-0.94L241.84,57.39z M244.3,58.09
            c-1.45,0-2.26,1.67-2.26,2.88c0,0.86,0.51,1.5,1.46,1.5c1.51,0,2.3-1.57,2.3-2.85C245.79,58.69,245.34,58.09,244.3,58.09z"
                                />
                                <path
                                  fill="#213970"
                                  d="M251.94,63.18h-0.92l0.18-0.87h-0.02c-0.48,0.65-1.27,1.01-2.06,1.01c-1.09,0-1.61-0.47-1.61-1.49
            c0-0.25,0.07-0.69,0.17-1.13l0.7-3.3h0.95l-0.74,3.38c-0.05,0.21-0.13,0.7-0.13,0.9c0,0.55,0.45,0.81,1.01,0.81
            c1.05,0,1.87-0.92,2.07-1.88l0.67-3.2h0.95L251.94,63.18z"
                                />
                                <path
                                  fill="#213970"
                                  d="M256.05,57.39h1.15l-0.18,0.84h-1.16l-0.72,3.4c-0.01,0.14-0.03,0.25-0.03,0.38
            c0,0.31,0.28,0.39,0.57,0.39c0.19,0,0.37-0.05,0.55-0.1l-0.17,0.87c-0.22,0.05-0.45,0.07-0.67,0.07c-0.71,0-1.3-0.2-1.3-0.94
            c0-0.17,0.02-0.3,0.05-0.48l0.76-3.6h-0.97l0.18-0.84h0.97l0.36-1.72h0.95L256.05,57.39z"
                                />
                                <path
                                  fill="#213970"
                                  d="M257.7,59.13c0.19-1.29,1.28-1.88,2.51-1.88c1.12,0,2.12,0.3,2.12,1.48c0,0.33-0.11,0.74-0.18,1.05
            l-0.37,1.58c-0.05,0.23-0.16,0.56-0.16,0.83c0,0.27,0.12,0.36,0.28,0.36c0.09,0,0.21-0.05,0.3-0.08l-0.15,0.7
            c-0.15,0.08-0.41,0.13-0.58,0.13c-0.53,0-0.81-0.31-0.75-0.81l-0.02-0.02c-0.4,0.53-0.97,0.84-1.94,0.84
            c-0.97,0-1.77-0.45-1.77-1.63c0-1.68,1.65-1.79,2.92-1.9c1.01-0.09,1.46-0.15,1.46-0.92c0-0.63-0.66-0.76-1.17-0.76
            c-0.73,0-1.45,0.29-1.56,1.04H257.7L257.7,59.13z M258.98,62.48c0.74,0,1.29-0.25,1.63-0.76c0.31-0.44,0.37-0.95,0.5-1.49h-0.02
            c-0.42,0.25-1.17,0.3-1.86,0.39c-0.68,0.1-1.29,0.31-1.29,1.05C257.94,62.21,258.45,62.48,258.98,62.48z"
                                />
                                <path
                                  fill="#213970"
                                  d="M267.58,59.23c0-0.71-0.48-1.13-1.22-1.13c-1.47,0-2.25,1.46-2.25,2.72c0,0.9,0.37,1.66,1.43,1.66
            c0.82,0,1.42-0.48,1.69-1.19h0.95c-0.33,1.29-1.31,2.03-2.64,2.03c-1.58,0-2.39-0.94-2.39-2.5c0-1.84,1.13-3.56,3.21-3.56
            c1.31,0,2.13,0.63,2.17,1.97H267.58z"
                                />
                                <path
                                  fill="#213970"
                                  d="M270.09,57.39h0.95l-1.21,5.79h-0.96L270.09,57.39z M271.27,56.35h-0.97l0.25-1.17h0.96L271.27,56.35z"
                                />
                                <path
                                  fill="#213970"
                                  d="M274.86,57.25c1.54,0,2.41,0.85,2.41,2.4c0,1.9-1.18,3.67-3.2,3.67c-1.53,0-2.44-1.02-2.44-2.42
            C271.62,59,272.83,57.25,274.86,57.25z M274.16,62.48c1.39,0,2.15-1.6,2.15-2.75c0-0.91-0.48-1.64-1.52-1.64
            c-1.46,0-2.22,1.59-2.22,2.79C272.57,61.84,273.12,62.48,274.16,62.48z M275.69,54.99h1.27l-1.52,1.6h-0.79L275.69,54.99z"
                                />
                                <path
                                  fill="#213970"
                                  d="M279.02,57.39h0.92l-0.18,0.87h0.02c0.48-0.65,1.27-1.01,2.06-1.01c1.09,0,1.62,0.47,1.62,1.49
            c0,0.25-0.07,0.69-0.17,1.13l-0.71,3.3h-0.95l0.74-3.38c0.04-0.21,0.13-0.69,0.13-0.9c0-0.55-0.45-0.81-1.01-0.81
            c-0.53,0-1.08,0.29-1.44,0.67c-0.33,0.29-0.51,0.75-0.64,1.21l-0.67,3.2h-0.96L279.02,57.39z"
                                />
                              </g>
                              <path
                                fill="#213970"
                                d="M44.56,4.65c2.74,0,8.51,1.25,10.64,1.85C33.37,5.45,19.62,20.24,19.62,37.24
          c0,12.1,7.23,25.45,24.61,29.45C24.07,67.05,12.3,50.42,12.3,35.91C12.3,17.54,28.06,4.65,44.56,4.65z"
                              />
                              <path
                                fill="#213970"
                                d="M32.92,0c3.57,0,8.19,1.17,11.47,2.16C43.03,1.99,40.74,2,39.91,2C19.75,2,7.65,19.7,7.65,36.08
          c0,14.09,7.81,29.02,26.6,33.27C11.98,69.92,0,50.42,0,34.58C0,16.17,14.63,0,32.92,0z"
                              />
                              <path
                                fill="#213970"
                                d="M40.01,37.01h-1.96l-0.45,2.15h2.28c0.63,0,1.5-0.23,1.5-1.16C41.38,37.14,40.75,37.01,40.01,37.01z"
                              />
                              <path
                                fill="#213970"
                                d="M51.81,9.07c-15.19,0-27.5,12.31-27.5,27.5c0,15.19,12.31,27.5,27.5,27.5c15.19,0,27.5-12.31,27.5-27.5
          C79.31,21.38,66.99,9.07,51.81,9.07z M44.93,38.03c0,2.78-2.23,3.88-4.75,3.88h-6.62l2.39-11.54h5.93c2.5,0,3.44,0.87,3.44,2.54
          c0,1.46-0.73,2.41-2.12,2.78h-0.02v0.03C44.26,35.95,44.93,36.96,44.93,38.03z M53.87,34.91c2,0.71,2.68,1.81,2.68,3.19
          c0,2.78-2.7,4.08-5.2,4.08c-2.65,0-5.04-1.21-4.9-4.15h3.44c0,0.63,0.15,1.02,0.44,1.31c0.31,0.26,0.73,0.29,1.31,0.29
          c0.69,0,1.55-0.35,1.55-1.11c0-0.84-1.18-0.96-2.78-1.49c-1.41-0.47-2.77-1.21-2.77-3.13c0-2.6,2.52-3.8,4.85-3.8
          c2.46,0,4.67,0.92,4.6,3.77h-3.28c0.02-0.47-0.14-0.76-0.4-0.96c-0.26-0.19-0.63-0.26-1.04-0.26c-0.6,0-1.37,0.18-1.37,0.94
          C51.01,34.46,52.74,34.51,53.87,34.91z M64.11,39.31c1.11,0,1.74-0.76,1.97-1.78h3.49c-0.21,1.18-1.11,4.65-5.91,4.65
          c-3.35,0-4.96-2.16-4.96-5.25c0-3.73,2.33-6.84,6.25-6.84c2.57,0,4.86,1.12,4.93,4.58h-3.36c0.03-1.08-0.49-1.7-1.58-1.7
          c-1.97,0-2.68,2.26-2.68,3.89C62.25,37.82,62.48,39.31,64.11,39.31z"
                              />
                              <path
                                fill="#213970"
                                d="M40.67,33.12h-1.83l-0.35,1.75h2c0.57,0,1.28-0.21,1.28-0.92C41.77,33.25,41.3,33.12,40.67,33.12z"
                              />
                            </g>
                          </g>
                        </svg>
                      }
                      color="#213970"
                      hoverColor="#000000"
                      mobileHeight="40px"
                      desktopHeight="50px"
                      alt="ESS Logo White"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* TeaserProfile Component */}
            <section className="components-showcase__section">
              <h2 className="components-showcase__section-title">
                TeaserProfile - Team Member Profiles
              </h2>
              <p className="components-showcase__description">
                Display team member information with image, name, position, and
                description. Designed for `team_member` content type. Uses Plone
                scale &quot;profile&quot; (500x500) for images.
              </p>
              <div className="components-showcase__grid">
                <div className="components-showcase__item">
                  <h4>Squared picture</h4>
                  <TeaserProfile
                    imageSrc="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=500&h=500&fit=crop&crop=face"
                    name="Sarah Johnson"
                    position="Senior Developer"
                    description="Full-stack developer specializing in React and Python"
                  />
                </div>
                <div className="components-showcase__item">
                  <h4>Non-squared picture</h4>
                  <TeaserProfile
                    imageSrc="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=450&fit=crop&crop=face"
                    name="Sarah Johnson"
                    position="Senior Developer"
                    description="Full-stack developer specializing in React and Python"
                  />
                </div>
                <div className="components-showcase__item">
                  <h4>Without Description</h4>
                  <TeaserProfile
                    imageSrc="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=500&h=500&fit=crop&crop=face"
                    name="Maria Garcia"
                    position="UX/UI Designer"
                  />
                </div>
                <div className="components-showcase__item">
                  <h4>With Link</h4>
                  <TeaserProfile
                    imageSrc="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=500&h=500&fit=crop&crop=face"
                    name="John Smith"
                    position="Project Manager"
                    description="Leading cross-functional teams"
                    href="/team/john-smith"
                  />
                </div>
                <div className="components-showcase__item">
                  <h4>Custom Width</h4>
                  <TeaserProfile
                    imageSrc="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=500&h=500&fit=crop&crop=face"
                    name="Emily Chen"
                    position="Data Scientist"
                    width="250px"
                  />
                </div>
              </div>

              <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>
                Color Customization
              </h3>
              <p className="components-showcase__description">
                Customize border, background, and text colors using the new color
                parameters.
              </p>
              <div className="components-showcase__grid">
                <div className="components-showcase__item">
                  <h4>Custom Border Color</h4>
                  <TeaserProfile
                    imageSrc="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=500&h=500&fit=crop&crop=face"
                    name="Sarah Johnson"
                    position="Senior Developer"
                    description="Full-stack developer specializing in React and Python"
                    borderColor="#0066cc"
                  />
                </div>
                <div className="components-showcase__item">
                  <h4>Custom Background Color</h4>
                  <TeaserProfile
                    imageSrc="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=500&h=500&fit=crop&crop=face"
                    name="Maria Garcia"
                    position="UX/UI Designer"
                    description="Creating beautiful user experiences"
                    backgroundColor="#f0f8ff"
                  />
                </div>
                <div className="components-showcase__item">
                  <h4>Custom Text Color</h4>
                  <TeaserProfile
                    imageSrc="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=500&h=500&fit=crop&crop=face"
                    name="John Smith"
                    position="Project Manager"
                    description="Leading cross-functional teams"
                    foregroundColor="#2c5282"
                  />
                </div>
                <div className="components-showcase__item">
                  <h4>All Colors Combined</h4>
                  <TeaserProfile
                    imageSrc="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=500&h=500&fit=crop&crop=face"
                    name="Emily Chen"
                    position="Data Scientist"
                    description="Analyzing complex datasets"
                    borderColor="#38a169"
                    backgroundColor="#f0fff4"
                    foregroundColor="#aaa"
                  />
                </div>
              </div>
            </section>

            {/* Accordion Component */}
            <section className="components-showcase__section">
              <h2 className="components-showcase__section-title">
                Accordion - Collapsible Content
              </h2>
              <p className="components-showcase__description">
                Collapsible accordion component for organizing content in expandable
                sections. Only one item can be open at a time.
              </p>
              <div className="components-showcase__full-width">
                <Accordion
                  items={[
                    {
                      id: 'faq1',
                      title: 'What is the ESS Design System?',
                      content: (
                        <p>
                          The ESS Design System is a comprehensive collection of
                          reusable components, design tokens, and guidelines built
                          on top of @plone/components for creating consistent and
                          accessible user interfaces.
                        </p>
                      ),
                    },
                    {
                      id: 'faq2',
                      title: 'How do I get started?',
                      content: (
                        <div>
                          <p>Getting started is easy:</p>
                          <ol>
                            <li>Install the required dependencies</li>
                            <li>Import the components you need</li>
                            <li>Follow the documentation and examples</li>
                          </ol>
                        </div>
                      ),
                    },
                    {
                      id: 'faq3',
                      title: 'Is it accessible?',
                      content: (
                        <p>
                          Yes! All components follow WCAG 2.1 accessibility
                          guidelines and include proper ARIA attributes, keyboard
                          navigation, and focus management.
                        </p>
                      ),
                    },
                    {
                      id: 'faq4',
                      title: 'Can I customize the styling?',
                      content: (
                        <p>
                          Absolutely! The ESS Design System uses CSS custom
                          properties (design tokens) that can be easily customized
                          to match your brand colors, spacing, typography, and more.
                        </p>
                      ),
                    },
                  ]}
                />
              </div>
            </section>

            {/* CTA Banner Component */}
            <section className="components-showcase__section">
              <h2 className="components-showcase__section-title">
                CTA Banner - Call to Action
              </h2>
              <p className="components-showcase__description">
                Full-width call-to-action banners with responsive layouts. Perfect
                for About Us, Contact, or promotional sections.
              </p>

              <div
                className="components-showcase__full-width"
                style={{ marginBottom: '2rem' }}
              >
                <h4 style={{ marginBottom: '1rem' }}>
                  Default Variant - Center Aligned
                </h4>
                <CTABanner
                  title="Ready to Get Started?"
                  description="Join thousands of organizations using our platform to build amazing experiences. Start your free trial today and see the difference."
                  ctaText="Start Free Trial"
                  ctaTo="/signup"
                  ctaVariant="positive"
                  variant="default"
                  alignment="center"
                />
              </div>

              <div
                className="components-showcase__full-width"
                style={{ marginBottom: '2rem' }}
              >
                <h4 style={{ marginBottom: '1rem' }}>
                  Dark Variant - About Us Style
                </h4>
                <CTABanner
                  title="About Our Research"
                  description="We are dedicated to advancing scientific knowledge through cutting-edge research and collaboration. Our team of experts works on groundbreaking projects that shape the future."
                  ctaText="Explore Our Work"
                  ctaTo="/research"
                  ctaVariant="negative"
                  variant="dark"
                  alignment="center"
                />
              </div>

              <div
                className="components-showcase__full-width"
                style={{ marginBottom: '2rem' }}
              >
                <h4 style={{ marginBottom: '1rem' }}>
                  Gradient Variant - Single CTA
                </h4>
                <CTABanner
                  title="Transform Your Workflow"
                  description="Streamline your processes and boost productivity with our innovative solutions."
                  ctaText="Get Started"
                  ctaHref="https://example.com"
                  ctaVariant="positive"
                  gradientStartColor="#aaa"
                  gradientEndColor="#000"
                  gradientDirection="to right"
                  ctaSize="md"
                  variant="gradient"
                  alignment="center"
                />
              </div>

              <div
                className="components-showcase__full-width"
                style={{ marginBottom: '2rem' }}
              >
                <h4 style={{ marginBottom: '1rem' }}>
                  Light Variant - Left Aligned
                </h4>
                <CTABanner
                  title="Need Help?"
                  description="Our support team is here to assist you with any questions or issues you may have."
                  ctaText="Contact Support"
                  ctaTo="/contact"
                  ctaVariant="accent"
                  variant="light"
                  alignment="left"
                />
              </div>

              <div
                className="components-showcase__full-width"
                style={{ marginBottom: '2rem' }}
              >
                <h4 style={{ marginBottom: '1rem' }}>
                  With Background Image - Custom Height & Text Color
                </h4>
                <CTABanner
                  title="Discover the Future of Science"
                  description="Join us in pushing the boundaries of research and innovation. Our state-of-the-art facilities and world-class team are ready to collaborate with you."
                  ctaText="Explore Opportunities"
                  ctaTo="/opportunities"
                  ctaVariant="positive"
                  backgroundImage="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=80"
                  textColor="#ffffff"
                  minHeight="500px"
                  alignment="center"
                />
              </div>

              <div className="components-showcase__full-width">
                <h4 style={{ marginBottom: '1rem' }}>
                  Independent Title & Description Colors
                </h4>
                <CTABanner
                  title="Custom Color Combinations"
                  description="Title and description can have different colors for maximum flexibility and design control."
                  ctaText="Get Started"
                  ctaTo="/start"
                  ctaVariant="negative"
                  backgroundColor="var(--color-brand-primary)"
                  titleColor="#ffffff"
                  descriptionColor="rgba(255, 255, 255, 0.85)"
                  minHeight="400px"
                  alignment="center"
                />
              </div>
            </section>

            {/* ========================================
                INTERACTIVE EXAMPLES
                Advanced component demonstrations
            ======================================== */}

            {/* Toast Component */}
            <section className="components-showcase__section">
              <h2 className="components-showcase__section-title">
                Toast - Notifications
              </h2>
              <p className="components-showcase__description">
                Click buttons to show toast notifications in different variants
              </p>
              <ToastExample />
            </section>

            {/* Tooltip Component */}
            <section className="components-showcase__section">
              <h2 className="components-showcase__section-title">
                Tooltip - Contextual Help
              </h2>
              <p className="components-showcase__description">
                Hover over buttons to see tooltips in different positions
              </p>
              <TooltipExample />
            </section>

            {/* Menu Component */}
            <section className="components-showcase__section">
              <h2 className="components-showcase__section-title">
                Menu - Dropdown Menus
              </h2>
              <p className="components-showcase__description">
                Click buttons to open dropdown menus with different variants
              </p>
              <MenuExample />
            </section>

            {/* Dialog Component */}
            <section className="components-showcase__section">
              <h2 className="components-showcase__section-title">
                Dialog - Modal Dialogs
              </h2>
              <p className="components-showcase__description">
                Click buttons to open modal dialogs in different sizes
              </p>
              <DialogExample />
            </section>

            {/* Cookie Consent Component */}
            <section className="components-showcase__section">
              <h2 className="components-showcase__section-title">
                Cookie Consent - GDPR Compliance
              </h2>
              <p className="components-showcase__description">
                GDPR-compliant cookie consent banner with Google Analytics 4
                integration
              </p>
              <CookieConsentExample />
            </section>
          </ContentWide>
        </ContentWrapper>
      </PageSection>
    </div>
  );
};

export default ComponentsShowcase;
